import {
  Transparencia,
  TransparenciaRaw,
  SubarticuloTransparencia,
} from "./types/transparencia";
import { fetchWithRetry } from "../client";
import { cmsLang, type Locale } from "@/i18n/config";

export async function fetchTransparencia(
  lang: Locale,
): Promise<Transparencia> {
  const res = await fetchWithRetry<{ data: TransparenciaRaw[] }>(
    `/transparencia?lang=${cmsLang(lang)}`,
  );
  const data = res.data[0];
  return {
    titulo: data.titulo,
    descripcion: data.descripcion,
    enlace_datos_abiertos: data.enlace_datos_abiertos,
    enlace_antai: data.enlace_antai,
    articulos: data.articulos.map((articulo) => ({
      id: articulo.id,
      codigo: articulo.codigo,
      nombre: articulo.nombre,
      label: articulo.label,
      subarticulos: articulo.subarticulos.map((subarticulo) => ({
        id: subarticulo.id,
        codigo: subarticulo.codigo,
        nombre: subarticulo.nombre,
        label: subarticulo.label,
        historico: subarticulo.historico,
        grupos: subarticulo.grupos.map((grupo) => ({
          id: grupo.id,
          nombre: grupo.nombre,
          documentosPorAnio: grupo.documentosPorAnio.map((docAnio) => ({
            anio: docAnio.anio,
            documentos: docAnio.documentos.map((documento) => ({
              id: documento.id,
              titulo: documento.titulo,
              archivo: documento.archivo,
              formato: documento.formato,
              anio: documento.anio,
              seguimientos: documento.seguimientos.map((seguimiento) => ({
                id: seguimiento.id,
                tipo: seguimiento.tipo,
                titulo: seguimiento.titulo,
                archivo: seguimiento.archivo,
                formato: seguimiento.formato,
              })),
            })),
          })),
        })),
        documentosSinGrupoPorAnio: subarticulo.documentosSinGrupoPorAnio.map(
          (documento) => ({
            anio: documento.anio,
            documentos: documento.documentos.map((doc) => ({
              id: doc.id,
              titulo: doc.titulo,
              archivo: doc.archivo,
              formato: doc.formato,
              anio: doc.anio,
              seguimientos: doc.seguimientos.map((seguimiento) => ({
                id: seguimiento.id,
                tipo: seguimiento.tipo,
                titulo: seguimiento.titulo,
                archivo: seguimiento.archivo,
                formato: seguimiento.formato,
              })),
            })),
          }),
        ),
      })),
    })),
  };
}

/**
 * Un subartículo suelto de Transparencia (p. ej. el 10.2, que alimenta la
 * página de Información Financiera).
 *
 * El CMS no expone los subartículos por separado: `/transparencia` devuelve el
 * árbol completo y hay que recortarlo aquí. Eso deja una sola fuente de verdad
 * —si el CMS reordena o renombra el 10.2, la página lo hereda sin tocar código—
 * a cambio de traerse el resto del payload y descartarlo.
 */
export async function fetchSubarticuloTransparencia(
  lang: Locale,
  codigo: string,
): Promise<SubarticuloTransparencia | null> {
  const data = await fetchTransparencia(lang);
  for (const articulo of data.articulos) {
    const subarticulo = articulo.subarticulos.find((s) => s.codigo === codigo);
    if (subarticulo) return { articulo, subarticulo };
  }
  return null;
}
